import { Box } from '@mui/material'
import ChartHeader from 'components/common/ChartHeader/ChartHeader'
import CountryTick from 'components/common/CountryTick/CountryTick'
import DownloadImages from 'components/common/Download/DownloadImages'
import { useImageDownload } from 'components/common/Download/useImageDownload'
import {
  DOWNLOAD_FILE_NAME,
  METRIC_ENDPOINT_RANK,
  METRIC_ENDPOINT_WEIGHT,
  SUB_INDICES_INFO,
  SubIndicesData,
} from 'constant'
import { scaleLinear } from 'd3-scale'
import { useActions, useAppSelector } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { BarChartGlobalData, DownloadType } from 'types'
import {
  isEmptyData,
  getCountryLongCodeFromCountryCode,
  generateGlobalBarChartData,
  calculateWeightedGlobalBarChart,
} from 'utils'
import * as Flags from 'country-flag-icons/react/3x2'
import _ from 'lodash'
import ChartFooter from '../common/ChartFooter/ChartFooter'
import SubIndicesChartComponent from 'components/Chart/SubIndices/SubIndicesChart'
import SubIndexTooltip from '../Chart/SubIndices/components/subIndexCustomTooltip'

const SubIndexRankingChart = () => {
  const fileNameDownload = DOWNLOAD_FILE_NAME.subIndicesChart
  const yearFilter = useAppSelector(({ vibrancy }) => vibrancy.configuration.yearFilter)
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricGroups = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)
  const { setRanking, setCountryType, setCountry, setShowDwnLoader } = useActions()
  const selectedMetricValue = useAppSelector(({ vibrancy }) => vibrancy.configuration.metricValue)
  const selectedSubIndex = useAppSelector(({ vibrancy }) => vibrancy.selectedSubIndex)
  const [clickDownload, setClickDownload] = useState<DownloadType>(null)
  const [subIndexData, setSubIndexData] = useState<null | SubIndicesData>(null)
  const yearSource = new Date().getFullYear()
  const { handleCallDownload, divRef } = useImageDownload()
  const handleDivDownload = (type: DownloadType) => {
    setShowDwnLoader(true)
    setClickDownload(type)
  }

  const data = useMemo<BarChartGlobalData>(() => {
    if (metricGroups.length === 0 || countryData.length === 0 || yearFilter.yearRange.length === 0) return []

    const includedIndicators = subIndexData?.indicators.flatMap((i) => [i.shortname_raw, i.shortname_raw_pc]) || []
    const filteredMetricGroups =
      metricGroups
        .filter((eachGroup) => subIndexData?.pillarList.includes(eachGroup.metricGroupNameDatakey))
        .map((i) => ({
          ...i,
          metrics: i.metrics.filter((j) => includedIndicators?.includes(j.shortname_raw as string)),
        })) || []
    const renderChartData =
      filteredMetricGroups &&
      calculateWeightedGlobalBarChart(filteredMetricGroups, selectedMetricValue, countryData, yearFilter.currentYear)
    const dataReturn = generateGlobalBarChartData(filteredMetricGroups, renderChartData, {
      globalYear: yearFilter.currentYear,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: selectedMetricValue,
    })
    return dataReturn
  }, [subIndexData, renderChartTimes])

  useEffect(() => {
    if (clickDownload) {
      handleCallDownload(clickDownload, {
        fileName: fileNameDownload[subIndexData?.indexCode || 'II'],
        orientation: 'portrait',
        isSquareDownload: true,
      })
      setClickDownload(null)
    }
  }, [clickDownload])

  useEffect(() => {
    const sdata = SUB_INDICES_INFO.find((i) => i.indexCode === selectedSubIndex)
    sdata && setSubIndexData(sdata)
  }, [selectedSubIndex])

  const renderTick = (dataParam: BarChartGlobalData) =>
    scaleLinear()
      .domain([0, _.max(dataParam.map((el) => el.score)) ?? 0])
      .ticks()
      .filter((el) => el % 10 === 0)

  const renderCustomAxisTick = ({ payload, y }: { payload: { value: string }; y: number }) => {
    return (
      <CountryTick
        countryCode={payload.value as keyof typeof Flags}
        id={data.find((el) => el.name === payload.value)?.id ?? 0}
        countryName={data.find((el) => el.name === payload.value)?.countryName ?? ''}
        position={{ y }}
        onClick={() => {
          setRanking({ ranking: 'national' })
          setCountryType({ countryType: 'national' })
          setCountry({ country: getCountryLongCodeFromCountryCode(countryData, payload.value) as string })
        }}
      />
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      <DownloadImages
        handleDownPNG={() => handleDivDownload('png')}
        handleDownJPG={() => handleDivDownload('jpg')}
        handleDownPDF={() => handleDivDownload('pdf')}
      />
      <Box sx={{ padding: '24px 24px 0px 24px' }}>
        <ChartHeader
          header1={`${yearFilter.currentYear} ${subIndexData?.indexName} ${selectedMetricValue === 'perCapita' ? '(Per Capita)' : '(Absolute)'}`}
          header2={`Source: ${yearSource} AI Index`}
        />
        <br />
        {!isEmptyData(data) && (
          <SubIndicesChartComponent
            data={data}
            isStackedBarChart
            xAxisTicks={renderTick(data)}
            yAxisTickRenderFunc={renderCustomAxisTick}
            CustomToolTipComponent={SubIndexTooltip}
            onClickBarChartEvent={(eleOnClick) => {
              setRanking({ ranking: 'national' })
              setCountryType({ countryType: 'national' })
              setCountry({ country: getCountryLongCodeFromCountryCode(countryData, eleOnClick.name) as string })
            }}
            subIndicesData={subIndexData}
          />
        )}
        <ChartFooter description={subIndexData?.description || ''} />
      </Box>
      <Box sx={{ zIndex: -1, position: 'absolute', padding: '24px 24px 0px 24px' }} ref={divRef}>
        <ChartHeader
          header1={`${yearFilter.currentYear} ${subIndexData?.indexName} ${selectedMetricValue === 'perCapita' ? '(Per Capita)' : '(Absolute)'}`}
          header2={`Source: ${yearSource} AI Index`}
        />
        <br />
        {!isEmptyData(data) && (
          <SubIndicesChartComponent
            data={data}
            isStackedBarChart
            xAxisTicks={renderTick(data)}
            yAxisTickRenderFunc={renderCustomAxisTick}
            CustomToolTipComponent={SubIndexTooltip}
            onClickBarChartEvent={(eleOnClick) => {
              setRanking({ ranking: 'national' })
              setCountryType({ countryType: 'national' })
              setCountry({ country: getCountryLongCodeFromCountryCode(countryData, eleOnClick.name) as string })
            }}
            is4Downloaded
            subIndicesData={subIndexData}
          />
        )}
        <ChartFooter description={subIndexData?.description || ''} />
      </Box>
    </Box>
  )
}

export default SubIndexRankingChart
